import { call, put, select, takeEvery } from 'redux-saga/effects'
import { AsyncAction, Action } from 'src/utils/reduxUtils'
import { FetchDriver, FetchPartner, SetDriver, SetPartner } from 'src/constants/actionTypes'
import { ApiService, callClientSecureApi } from './api/callApi'
import { Endpoints } from 'src/constants/endpoints'
import { Driver } from 'src/interfaces/driver'
import { Partner } from 'src/interfaces/partner'
import { captureSentryError } from 'src/utils/sentry'
import * as Sentry from '@sentry/react'
import { logger } from 'src/utils/logger'
import { getDriver } from 'src/selectors/driver'
import { getPartner } from 'src/selectors/partner'
import { getApiErrorMessage } from 'src/utils/errorHandlers'
import { writeCachedDriver } from 'src/reducers/driver'

function* onFetchPartner(action: AsyncAction<void, Partner>) {
  try {
    const { payload }: { payload: Partner } = yield call(
      callClientSecureApi,
      Endpoints.FetchPartner,
      {
        method: 'GET',
        apiService: ApiService.Paraworks,
      },
    )

    yield put(FetchPartner.success(payload))
    yield put(SetPartner.success(payload))

    const driver: Driver | null = yield select(getDriver)

    if (driver) {
      logger.identify({
        id: driver.id,
        name: `${driver.firstName} ${driver.lastName}`,
        email: driver.email,
        partnerId: payload.id,
        partnerName: payload.name,
      })
    }

    if (action.next) {
      action.next(null, payload)
    }
  } catch (err: any) {
    captureSentryError(err, {
      message: '[saga.driver.onFetchPartner] error',
    })

    const errorMessage = getApiErrorMessage(err, 'Failed to fetch partner')

    yield put(FetchPartner.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

function* onFetchDriver(action: AsyncAction<void, Driver>) {
  try {
    const { payload }: { payload: Driver } = yield call(
      callClientSecureApi,
      Endpoints.FetchDriver,
      {
        apiService: ApiService.Paraworks,
      },
    )

    yield put(FetchDriver.success())
    yield put(SetDriver.request(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (err: any) {
    captureSentryError(err, {
      message: '[saga.driver.onFetchDriver] error',
    })

    const errorMessage = getApiErrorMessage(err, 'Failed to fetch driver')

    yield put(FetchDriver.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

function* onSetDriverSuccess(action: Action<Driver | null>) {
  const driver = action.payload

  writeCachedDriver(driver)

  yield put(SetDriver.success(driver))

  if (driver) {
    const partner: Partner | null = yield select(getPartner)
    logger.identify({
      id: driver.id,
      name: `${driver.firstName} ${driver.lastName}`,
      email: driver.email,
      ...(partner && {
        partnerId: partner.id,
        partnerName: partner.name,
      }),
    })

    Sentry.setUser({ id: driver.id, email: driver.email })

    logger.onSessionURLReady((url: string) => {
      Sentry.setTag('logrocket_session', url)
    })
  } else {
    Sentry.setUser(null)
  }
}

export function* driverSaga() {
  yield takeEvery(FetchPartner.type.REQUEST, onFetchPartner)
  yield takeEvery(FetchDriver.type.REQUEST, onFetchDriver)
  yield takeEvery(SetDriver.type.REQUEST, onSetDriverSuccess)
}
